import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useState} from "react";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {
    useRateInteraction,
    useSetAIGuideInteractionReaction,
} from "@khan/guide-core";
import {useGuideState} from "@khan/guide-state";
import {
    FeedbackType,
    type EmojiSentiment,
    type InteractionID,
} from "@khan/guide-types";

import {MessageReactionButton} from "./message-reaction-button.tsx";

import checkIcon from "@phosphor-icons/core/bold/check-bold.svg";

interface Props {
    threadId: string;
    interactionId: InteractionID;
    onShowFeedbackForm: () => unknown;
}

/**
 * MessageReactionSelector displays controls for rating the quality of the AIGuide's
 * response. When a reaction is selected, it renders the selected reaction.
 */
const MessageReactionSelector = (props: Props): React.ReactElement => {
    const {threadId, interactionId, onShowFeedbackForm} = props;
    const [isLoading, setLoading] = useState(false);
    const ratedInteractions = useGuideState("ratedInteractions");
    const rateInteraction = useRateInteraction();
    const setAIGuideInteractionReaction = useSetAIGuideInteractionReaction();
    const handleSentimentSelected = useCallback(
        async (sentiment: EmojiSentiment) => {
            setLoading(true);
            rateInteraction(interactionId, {
                sentiment,
                type: FeedbackType.Quick,
                positive: sentiment === "thumbs-up",
            });
            try {
                await setAIGuideInteractionReaction({
                    threadId,
                    interactionId,
                    sentiment,
                });
            } catch (error: any) {
                // TODO(tony): We'll maybe want to do something when we
                // encounter an error here. For now we'll do nothing.
            }
            setLoading(false);
        },
        [
            threadId,
            interactionId,
            rateInteraction,
            setAIGuideInteractionReaction,
        ],
    );

    const rating = ratedInteractions.get(interactionId);
    const selectedSentiment = rating?.sentiment;
    const selectedType = rating?.type;
    // We can still rate if written feedback has been submitted
    const submittedEmojiSentiment = selectedSentiment != null;
    const canRate = !submittedEmojiSentiment && !isLoading;
    const submittedFeedback =
        selectedType != null && selectedType !== FeedbackType.Quick;

    return (
        <View style={[styles.reactionSelector, styles.gap]}>
            {!submittedFeedback && (
                <Button
                    onClick={onShowFeedbackForm}
                    kind="tertiary"
                    testId="leave-feedback"
                    style={{width: "min-content"}}
                    size="small"
                >
                    {t`Leave feedback`}
                </Button>
            )}
            {!submittedFeedback && !submittedEmojiSentiment && (
                <View style={styles.reactionSelector}>
                    <MessageReactionButton
                        sentiment="thumbs-up"
                        disabled={!canRate}
                        onClick={handleSentimentSelected}
                    />
                    <MessageReactionButton
                        sentiment="thumbs-down"
                        disabled={!canRate}
                        onClick={handleSentimentSelected}
                    />
                </View>
            )}
            {submittedFeedback && (
                <View style={[styles.feedbackSent]}>
                    <PhosphorIcon icon={checkIcon} size="small" />
                    <Strut size={spacing.xxxSmall_4} />
                    <LabelXSmall>
                        {t`Thanks! While we can't reply to each comment, we read them daily.`}
                    </LabelXSmall>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    feedbackSent: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        color: semanticColor.core.foreground.neutral.default,
    },
    reactionSelector: {
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.xxxSmall_4,
    },
    gap: {
        gap: spacing.xxSmall_6,
    },
});

export {MessageReactionSelector};
